import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  ArrowUpRight, CalendarClock, ClipboardList, Sparkles, ShieldAlert, ChevronRight,
  CheckCircle2, Circle, AlertCircle,
} from 'lucide-react';
import api from '../lib/api';
import ScoreGauge from '../components/ScoreGauge';
import SlaAlertsWidget from '../components/SlaAlertsWidget';
import { PRIORITY, SCORE_LEVELS, formatDate, formatDateTime } from '../lib/constants';
import { useAuth } from '../lib/auth';
import { useRouteProgress, ROUTE_STEPS } from '../lib/useRouteProgress';

/**
 * Priviax · Dashboard
 *
 * Jerarquía visual (senior compliance UI):
 * 1. Meta strip — quién / cuándo / rerun.
 * 2. Overview: score gauge + evolución de score (bloque principal).
 * 3. Resumen ejecutivo — bloque editorial dedicado, no incrustado.
 * 4. KPIs — tira horizontal densa, con divisores hairline, sin cards separadas.
 * 5. Categorías (tabla densa) + Próximas acciones (lista).
 * 6. Alertas activas — sección discreta al final si aplica.
 */

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { organization } = useAuth();
  const navigate = useNavigate();
  const industry = organization?.industria || 'otro';
  const { progress, done: routeDone, total: routeTotal, nextStep } = useRouteProgress();

  useEffect(() => {
    (async () => {
      try {
        const { data: dash } = await api.get('/dashboard');
        setData(dash);
      } finally {
        setLoading(false);
      }
    })();
  }, [industry]);

  if (loading) return <div className="text-sm text-[#6B7280]">Cargando dashboard…</div>;

  // Empty state
  if (!data?.latest_diagnostic) {
    return (
      <div className="space-y-8" data-testid="dashboard-empty">
        <div className="max-w-3xl border border-[#E5E7EB] bg-white p-10">
          <div className="pv-eyebrow">Comencemos</div>
          <h2 className="mt-2 text-2xl font-semibold text-[#1C2329] tracking-tight">
            Aún no hay diagnóstico para {organization?.razon_social}
          </h2>
          <p className="text-sm text-[#6B7280] mt-2 max-w-xl leading-relaxed">
            Responda 15 preguntas simples (10–15 min) para calcular su score de cumplimiento de la Ley 21.719 y generar un plan de acción priorizado.
          </p>
          <button
            className="mt-6 pv-btn-primary inline-flex items-center gap-2"
            onClick={() => navigate('/app/diagnostico')}
            data-testid="empty-start-diagnostic"
          >
            Iniciar diagnóstico <ArrowUpRight size={15} />
          </button>
        </div>
        {progress && (
          <RouteSection progress={progress} done={routeDone} total={routeTotal} nextStep={nextStep} navigate={navigate} />
        )}
      </div>
    );
  }

  const d = data.latest_diagnostic;
  const meta = SCORE_LEVELS[d.level] || SCORE_LEVELS.critico;
  const chartData = (data.history || []).map((h) => ({
    fecha: formatDate(h.created_at),
    score: h.score,
  }));

  // Sort categories weakest → strongest (útil para narrativa)
  const sortedCats = [...d.category_scores].sort((a, b) => a.score - b.score);
  const weakest = sortedCats[0];
  const strongest = sortedCats[sortedCats.length - 1];

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      {/* ─── Meta strip ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3 border-b border-[#E5E7EB] pb-5" data-testid="dashboard-meta">
        <MetaItem label="Diagnóstico">
          <span className="font-mono text-[13px] text-[#1C2329]">#{d.id.slice(0, 8)}</span>
        </MetaItem>
        <MetaItem label="Realizado">
          <span className="text-[13.5px] text-[#1C2329]">{formatDateTime(d.created_at)}</span>
        </MetaItem>
        <MetaItem label="Nivel">
          <span
            className="inline-flex items-center gap-1.5 text-[12px] font-medium px-2 py-0.5 rounded-sm"
            style={{ background: meta.bg, color: meta.text, border: `1px solid ${meta.color}33` }}
            data-testid="meta-level"
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
            {meta.label}
          </span>
        </MetaItem>
        <div className="flex-1" />
        <button
          className="text-[13px] text-[#374151] hover:text-[#143A34] inline-flex items-center gap-1.5"
          onClick={() => navigate('/app/diagnostico')}
          data-testid="rerun-diagnostic"
        >
          Rehacer diagnóstico <ArrowUpRight size={13} />
        </button>
      </div>

      {/* ─── SLA en riesgo (brechas + ARCO+) ─────────────────────────── */}
      <SlaAlertsWidget alerts={data.sla_alerts} />

      {/* ─── Overview: Score + Evolución ────────────────────────────── */}
      <section className="grid grid-cols-1 xl:grid-cols-12 gap-6" data-testid="dashboard-overview">
        {/* Score card */}
        <div className="xl:col-span-4 border border-[#E5E7EB] bg-white p-6 flex flex-col">
          <div className="flex items-baseline justify-between mb-3">
            <span className="pv-eyebrow">Score global</span>
            <span className="text-[11px] text-[#9CA3AF] font-mono">0–100</span>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <ScoreGauge score={d.score} level={d.level} size={220} label="cumplimiento Ley 21.719" />
          </div>
          {progress && (
            <button
              onClick={() => navigate(nextStep ? nextStep.to : '/app/fiscalizacion')}
              className="mt-3 w-full flex items-center justify-between gap-2 border border-[#E5E7EB] px-3 py-2 hover:bg-[#FAFBFC] transition-colors text-left"
              data-testid="score-route-progress"
            >
              <div className="min-w-0">
                <div className="text-[10.5px] uppercase tracking-wider text-[#6B7280] font-medium">Tu ruta</div>
                <div className="text-[12.5px] text-[#1C2329] mt-0.5 truncate">
                  <span className="font-semibold text-[#143A34] tabular-nums">{routeDone}/{routeTotal}</span> pasos listos
                  {nextStep ? ` · sigue: ${nextStep.title}` : ' · todo al día'}
                </div>
              </div>
              <ChevronRight size={14} className="text-[#9CA3AF] shrink-0" />
            </button>
          )}
          <div className="mt-4 pt-4 border-t border-[#E5E7EB] grid grid-cols-2 gap-4 text-[12px]">
            <div>
              <div className="pv-eyebrow">Fortaleza</div>
              <div className="mt-1 text-[13px] text-[#1C2329] leading-tight">{strongest.category_label}</div>
              <div className="text-[11px] text-[#065F46] font-mono">{strongest.score}/100</div>
            </div>
            <div>
              <div className="pv-eyebrow">Mayor brecha</div>
              <div className="mt-1 text-[13px] text-[#1C2329] leading-tight">{weakest.category_label}</div>
              <div className="text-[11px] font-mono" style={{ color: SCORE_LEVELS[levelFor(weakest.score)].color }}>
                {weakest.score}/100
              </div>
            </div>
          </div>
        </div>

        {/* Evolution */}
        <div className="xl:col-span-8 border border-[#E5E7EB] bg-white p-6" data-testid="dashboard-history">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="pv-eyebrow">Evolución del score</div>
              <div className="text-[15px] font-medium text-[#1C2329] mt-1">
                {chartData.length > 1 ? `${chartData.length} diagnósticos en el tiempo` : 'Su primer diagnóstico'}
              </div>
            </div>
            <span className="text-[11px] text-[#9CA3AF] font-mono">objetivo · 85+</span>
          </div>
          <div className="h-56">
            {chartData.length >= 2 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="scoreG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2A9B68" stopOpacity={0.22} />
                      <stop offset="100%" stopColor="#2A9B68" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke="#EEF0F3" vertical={false} />
                  <XAxis dataKey="fecha" tickLine={false} axisLine={false} fontSize={11} tick={{ fill: '#6B7280' }} />
                  <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickLine={false} axisLine={false} width={36} fontSize={11} tick={{ fill: '#6B7280' }} />
                  <Tooltip contentStyle={{ border: '1px solid #E5E7EB', borderRadius: 4, background: '#fff', fontSize: 12 }} />
                  <Area type="monotone" dataKey="score" stroke="#2A9B68" strokeWidth={2} fill="url(#scoreG)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <SinglePointEvolution current={d.score} label={formatDate(d.created_at)} />
            )}
          </div>
        </div>
      </section>

      {/* ─── Resumen ejecutivo ─────────────────────────────────────── */}
      <section className="border-l-2 border-[#2A9B68] bg-white pl-6 pr-4 py-5 border-y border-r border-y-[#E5E7EB] border-r-[#E5E7EB]" data-testid="dashboard-summary">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={13} className="text-[#2A9B68]" strokeWidth={1.5} />
          <span className="pv-eyebrow">Resumen ejecutivo</span>
        </div>
        <p className="text-[14.5px] text-[#1C2329] leading-relaxed max-w-4xl whitespace-pre-line">
          {d.ai_summary}
        </p>
      </section>

      {/* ─── KPIs strip ─────────────────────────────────────────────── */}
      <section className="border border-[#E5E7EB] bg-white" data-testid="dashboard-kpis">
        <div className="grid grid-cols-2 md:grid-cols-6 divide-x divide-[#E5E7EB]">
          <Kpi label="Pendientes" value={data.kpis.tareas_pendientes} tone="#DC2626" testId="kpi-pendientes" />
          <Kpi label="En curso" value={data.kpis.tareas_en_curso} tone="#2563EB" testId="kpi-en-curso" />
          <Kpi label="Completadas" value={data.kpis.tareas_completadas} tone="#2A9B68" testId="kpi-completadas" />
          <Kpi label="Consentimientos" value={data.kpis.consentimientos_activos} hint="Próximo" muted testId="kpi-consent" />
          <Kpi label="ARCO+ activos" value={data.kpis.arco_pendientes} hint="Próximo" muted testId="kpi-arco" />
          <Kpi label="DPIA vigentes" value={data.kpis.dpias_vigentes} hint="Después" muted testId="kpi-dpia" />
        </div>
      </section>

      {/* ─── Tu ruta de cumplimiento (7 pasos) ─────────────────────── */}
      {progress && (
        <RouteSection progress={progress} done={routeDone} total={routeTotal} nextStep={nextStep} navigate={navigate} />
      )}

      {/* ─── Categorías + Próximas acciones ─────────────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Category table */}
        <div className="lg:col-span-3 border border-[#E5E7EB] bg-white" data-testid="dashboard-categories">
          <div className="flex items-baseline justify-between px-6 py-4 border-b border-[#E5E7EB]">
            <div>
              <div className="pv-eyebrow">Desglose por categoría</div>
              <div className="text-[15px] font-medium text-[#1C2329] mt-1">8 áreas evaluadas</div>
            </div>
            <span className="text-[11px] text-[#9CA3AF] font-mono">{d.category_scores.length} de 8</span>
          </div>
          <table className="pv-table">
            <thead>
              <tr>
                <th>Categoría</th>
                <th style={{ width: 130 }}>Score</th>
                <th style={{ width: 80 }} className="text-right">Peso</th>
              </tr>
            </thead>
            <tbody>
              {d.category_scores.map((c) => {
                const lvl = levelFor(c.score);
                const cmeta = SCORE_LEVELS[lvl];
                return (
                  <tr key={c.category_id}>
                    <td>
                      <div className="text-[13.5px] font-medium text-[#1C2329]">{c.category_label}</div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 pv-seg-bar" style={{ maxWidth: 80 }}>
                          <span style={{ width: `${c.score}%`, background: cmeta.color }} />
                        </div>
                        <span className="tabular-nums text-[13px] font-medium" style={{ color: cmeta.color }}>
                          {c.score}
                        </span>
                      </div>
                    </td>
                    <td className="text-right font-mono text-[12px] text-[#6B7280]">
                      {(c.weight * 100).toFixed(0)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Top pending */}
        <div className="lg:col-span-2 border border-[#E5E7EB] bg-white" data-testid="dashboard-top-pendientes">
          <div className="flex items-baseline justify-between px-6 py-4 border-b border-[#E5E7EB]">
            <div>
              <div className="pv-eyebrow">Su próxima misión</div>
              <div className="text-[15px] font-medium text-[#1C2329] mt-1">Prioridad alta</div>
            </div>
            <button
              className="text-[12px] text-[#2A9B68] hover:text-[#228256] inline-flex items-center gap-1"
              onClick={() => navigate('/app/plan')}
              data-testid="see-full-plan"
            >
              Ver plan <ChevronRight size={12} />
            </button>
          </div>
          {data.top_pendientes.length === 0 ? (
            <div className="p-6 text-sm text-[#6B7280]">Sin tareas de alta prioridad pendientes.</div>
          ) : (
            <ol className="divide-y divide-[#E5E7EB]">
              {data.top_pendientes.map((t, idx) => (
                <li key={t.id} className="px-6 py-4 flex gap-3" data-testid={`top-task-${t.id}`}>
                  <span className="font-mono text-[11px] text-[#9CA3AF] pt-0.5">{String(idx + 1).padStart(2, '0')}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-medium text-[#1C2329] leading-snug">{t.title}</div>
                    <div className="mt-1.5 flex items-center gap-3 text-[11.5px] text-[#6B7280]">
                      <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: PRIORITY[t.priority].color }} />
                        {PRIORITY[t.priority].label}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock size={11} /> {t.estimated_minutes} min
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      {/* ─── Alertas activas (solo si aplica) ───────────────────────── */}
      {data.alertas?.length > 0 && (
        <section data-testid="dashboard-alerts">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert size={14} className="text-[#DC2626]" strokeWidth={1.5} />
            <span className="pv-eyebrow text-[#DC2626]">Alertas activas · {data.alertas.length}</span>
          </div>
          <div className="border border-[#E5E7EB] bg-white divide-y divide-[#E5E7EB]">
            {data.alertas.map((a) => (
              <div key={a.id} className="flex items-start gap-4 px-6 py-4">
                <span className="w-1 self-stretch bg-[#DC2626] rounded-sm mt-0.5" />
                <div className="flex-1">
                  <div className="text-[14px] font-medium text-[#1C2329]">{a.titulo}</div>
                  <div className="text-[13px] text-[#6B7280] mt-0.5">{a.mensaje}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function MetaItem({ label, children }) {
  return (
    <div>
      <div className="pv-eyebrow mb-1">{label}</div>
      {children}
    </div>
  );
}

function Kpi({ label, value, tone, hint, muted, testId }) {
  return (
    <div className={`px-5 py-4 ${muted ? 'opacity-70' : ''}`} data-testid={testId}>
      <div className="pv-eyebrow">{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-[26px] font-semibold tabular-nums leading-none" style={{ color: tone || '#1C2329' }}>
          {value ?? '—'}
        </span>
        {hint && <span className="text-[10px] text-[#9CA3AF] font-mono uppercase tracking-wider">{hint}</span>}
      </div>
    </div>
  );
}

function SinglePointEvolution({ current, label }) {
  return (
    <div className="h-full flex flex-col justify-end">
      <div className="text-[11px] text-[#9CA3AF] font-mono uppercase tracking-wider mb-2">Punto base</div>
      <div className="flex items-end gap-4 border-b border-[#E5E7EB] pb-4">
        <div>
          <div className="text-[36px] font-semibold tabular-nums text-[#1C2329] leading-none">{current}</div>
          <div className="text-[12px] text-[#6B7280] mt-1">{label}</div>
        </div>
        <div className="flex-1 h-px bg-[#E5E7EB] mb-3" />
      </div>
      <div className="mt-3 text-[12px] text-[#6B7280] flex items-center gap-1.5">
        <ClipboardList size={12} strokeWidth={1.5} />
        Rehaga el diagnóstico en dos semanas para ver la evolución.
      </div>
    </div>
  );
}

function levelFor(score) {
  if (score >= 85) return 'excelente';
  if (score >= 70) return 'bueno';
  if (score >= 50) return 'medio';
  if (score >= 30) return 'bajo';
  return 'critico';
}

/* ─── Tu ruta de cumplimiento: 7 pasos guiados ─────────────────── */

function RouteSection({ progress, done, total, nextStep, navigate }) {
  const complete = done === total;
  const pct = Math.round((done / total) * 100);

  return (
    <section data-testid="dashboard-route">
      <div className="flex items-baseline justify-between mb-3">
        <span className="pv-eyebrow">Centro de misiones · Tu ruta Ley 21.719</span>
        <div className="text-[12px] font-mono text-[#374151] tabular-nums" data-testid="route-counter">
          {done}/{total} · {pct}%
        </div>
      </div>

      {nextStep && (
        <button
          onClick={() => navigate(nextStep.to)}
          className="mb-3 w-full border-l-2 border-l-[#2A9B68] border-y border-r border-y-[#E5E7EB] border-r-[#E5E7EB] bg-white px-5 py-3.5 flex items-center justify-between gap-3 text-left hover:bg-[#FAFBFC] transition-colors"
          data-testid="route-next-step"
        >
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#2A9B68] font-semibold">Siguiente paso de tu ruta</div>
            <div className="text-[14px] font-medium text-[#1C2329] mt-0.5">{nextStep.title}</div>
          </div>
          <ArrowUpRight size={15} className="text-[#2A9B68] shrink-0" />
        </button>
      )}

      <div className={`border ${complete ? 'border-[#A7F3D0] bg-[#ECFDF5]' : 'border-[#E5E7EB] bg-white'}`}>
        {complete && (
          <div className="px-6 py-3 border-b border-[#A7F3D0] bg-[#D1FAE5] flex items-center gap-2 text-[13px] text-[#065F46]" data-testid="route-complete">
            <CheckCircle2 size={14} strokeWidth={2} />
            <span><b>Ruta completa.</b> Su organización recorrió los 7 pasos de cumplimiento de la Ley 21.719.</span>
          </div>
        )}
        <div className="divide-y divide-[#E5E7EB]">
          {ROUTE_STEPS.map((s, idx) => {
            const st = progress[s.to];
            return (
              <button
                key={s.key}
                onClick={() => navigate(s.to)}
                className="w-full text-left px-6 py-3.5 flex items-start gap-3 hover:bg-[#FAFBFC] transition-colors"
                data-testid={`route-step-${s.key}`}
              >
                <RouteIcon status={st} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-medium text-[#1C2329]">
                    <span className="font-mono text-[11px] text-[#9CA3AF] mr-2">{String(idx + 1).padStart(2, '0')}</span>
                    {s.title}
                  </div>
                  <div className={`mt-0.5 text-[12px] ${st === 'done' ? 'text-[#065F46]' : st === 'attention' ? 'text-[#92400E]' : 'text-[#6B7280]'}`}>
                    {st === 'done' ? 'Listo' : st === 'attention' ? 'Requiere atención' : 'Pendiente'}
                  </div>
                </div>
                <ChevronRight size={14} className="text-[#9CA3AF] mt-0.5 shrink-0" />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function RouteIcon({ status }) {
  if (status === 'done') return <CheckCircle2 size={16} strokeWidth={2} className="text-[#2A9B68] mt-0.5" />;
  if (status === 'attention') return <AlertCircle size={16} strokeWidth={2} className="text-[#F59E0B] mt-0.5" />;
  return <Circle size={16} strokeWidth={2} className="text-[#D1D5DB] mt-0.5" />;
}
